import {
  V4_TYPE3_OFFSETS,
  assertType3Packet,
  clampToRange as clamp,
  toByte,
} from './PerformanceType3';

/**
 * 4.0 协议「按键性能」（原 HITS 配置）字段层。
 *
 * 数据来源是性能数据 command=4 / type=3（读）的完整包，写回时 type 改成 4，
 * 偏移见 PerformanceType3。本页负责的是：
 *
 *   26 keyScanRate（bit7 使能按键扫描频率，bit0-2 频率档位）
 *   27 buttonType（按键类型位域，只读，只展示不下发）
 *   28 / 29 左右键模式，30 快速响应模式位域
 *   31-36 左/右/其他按键的按下、放开防抖时间（ms）
 *   37-38 / 41-42 左右键 RT 触碰点（按下/回弹的触发深度，档位 1-10，回弹 ≤ 按下）
 *   39-40 / 43-44 左右键马达：
 *     MotorType bit7 按下震动（0 开 1 关）、bit6 放开震动（0 开 1 关）、
 *     bit3-5 震动时长（0–7 → 3–10ms）、bit0-2 震动比例（0–3 → 25/50/75/100%）；
 *     MotorLevel 0–63 微调
 */

/**
 * byte27：硬件按键类型位域，固件上报，驱动不可写。
 *   bit0-3 按键类型（取值见 BUTTON_TYPE）
 *   bit4-5 左键当前模块、bit6-7 右键当前模块（编码同 BUTTON_TYPE 低两位，
 *          可换模块机型用来检测用户是否更换了微动模块）
 */
export const BUTTON_TYPE = Object.freeze({
  MECHANICAL: 0,
  OPTICAL: 1,
  MECHANICAL_OPTICAL_SHARED: 2,
  HALL: 3,
  PRESSURE: 4,
});

export const BUTTON_TYPE_BITS = Object.freeze({
  BASE_MASK: 0x0f,
  LEFT_MODULE_SHIFT: 4,
  RIGHT_MODULE_SHIFT: 6,
  MODULE_MASK: 0x03,
});

/** byte27 → { baseType, leftModule, rightModule } */
export const decodeButtonTypeByte = (byte) => {
  const value = toByte(byte);
  return {
    baseType: value & BUTTON_TYPE_BITS.BASE_MASK,
    leftModule:
      (value >> BUTTON_TYPE_BITS.LEFT_MODULE_SHIFT) &
      BUTTON_TYPE_BITS.MODULE_MASK,
    rightModule:
      (value >> BUTTON_TYPE_BITS.RIGHT_MODULE_SHIFT) &
      BUTTON_TYPE_BITS.MODULE_MASK,
  };
};

export const BUTTON_TYPE_LABELS = Object.freeze({
  [BUTTON_TYPE.MECHANICAL]: '机械按键',
  [BUTTON_TYPE.OPTICAL]: '光微动',
  [BUTTON_TYPE.MECHANICAL_OPTICAL_SHARED]: '机械微动 / 光微动共板',
  [BUTTON_TYPE.HALL]: '霍尔按键',
  [BUTTON_TYPE.PRESSURE]: '压力按键',
});

/** byte28 / byte29：左右键识别模式 */
export const BUTTON_MODE = Object.freeze({
  AUTO: 0,
  MECHANICAL: 1,
  INFRARED: 2,
  ANALOG: 3,
});

export const BUTTON_MODE_LABELS = Object.freeze({
  [BUTTON_MODE.AUTO]: '自动',
  [BUTTON_MODE.MECHANICAL]: '机械按键',
  [BUTTON_MODE.INFRARED]: '红外按键',
  [BUTTON_MODE.ANALOG]: '模拟按键',
});

/** byte30：按键快速响应模式位域 */
export const FAST_BUTTON_BITS = Object.freeze({
  leftFastResponse: 0,
  rightFastResponse: 1,
  leftForceDelay: 3,
  rightForceDelay: 4,
});

/**
 * byte39 / byte43：马达类型。
 *   bit7 按下震动、bit6 放开震动：都是 0 开 1 关
 *   bit3-5 震动时长：0-7 → 3ms-10ms（档位 + 3ms）
 *   bit0-2 震动比例：0=25%、1=50%、2=75%、3=100%
 */
export const MOTOR_PRESS_OFF_BIT = 0x80;
export const MOTOR_RELEASE_OFF_BIT = 0x40;
export const MOTOR_RATIO_MASK = 0x07;
export const MOTOR_DURATION_SHIFT = 3;
export const MOTOR_DURATION_MASK = 0x38;

export const MOTOR_RATIO_RANGE = Object.freeze({ min: 0, max: 3 });
export const MOTOR_RATIO_PERCENTS = Object.freeze([25, 50, 75, 100]);
export const MOTOR_DURATION_RANGE = Object.freeze({ min: 0, max: 7 });
export const MOTOR_DURATION_BASE_MS = 3;
export const MOTOR_DURATION_MS_RANGE = Object.freeze({
  min: MOTOR_DURATION_RANGE.min + MOTOR_DURATION_BASE_MS,
  max: MOTOR_DURATION_RANGE.max + MOTOR_DURATION_BASE_MS,
});

export const motorDurationToMs = (duration) =>
  clamp(duration, MOTOR_DURATION_RANGE, MOTOR_DURATION_RANGE.min) +
  MOTOR_DURATION_BASE_MS;

export const msToMotorDuration = (ms) =>
  clamp(
    Number(ms) - MOTOR_DURATION_BASE_MS,
    MOTOR_DURATION_RANGE,
    MOTOR_DURATION_RANGE.min
  );

/** 协议默认值，用于设备没给出有效值时兜底 */
export const BUTTON_PERFORMANCE_DEFAULTS = Object.freeze({
  debouncePress: 4,
  debounceFree: 4,
  rtPress: 2,
  rtFree: 2,
  motorType: 0,
  motorLevel: 0,
});

/** 界面允许的取值区间（协议本身是 uint8，这里只是保守的可用范围） */
export const DEBOUNCE_RANGE = Object.freeze({ min: 0, max: 20 });
/** RT 触碰点（触发深度）档位：模拟按键共 10 档，byte37/38/41/42 取值 1-10 */
export const RT_RANGE = Object.freeze({ min: 1, max: 10 });
export const MOTOR_LEVEL_RANGE = Object.freeze({ min: 0, max: 63 });

export const getMotorRatioPercent = (ratio) =>
  MOTOR_RATIO_PERCENTS[Number(ratio)] ?? null;

export const getMotorRatioLabel = (ratio) => {
  const percent = getMotorRatioPercent(ratio);
  return percent != null ? `${percent}%` : `未知比例 (${ratio})`;
};

/**
 * byte39 / byte43 →
 *   { motorPressEnabled, motorReleaseEnabled, motorDuration, motorRatio }
 */
export const decodeMotorType = (byte) => {
  const value = toByte(byte);
  return {
    motorPressEnabled: (value & MOTOR_PRESS_OFF_BIT) === 0,
    motorReleaseEnabled: (value & MOTOR_RELEASE_OFF_BIT) === 0,
    motorDuration: (value & MOTOR_DURATION_MASK) >> MOTOR_DURATION_SHIFT,
    motorRatio: clamp(
      value & MOTOR_RATIO_MASK,
      MOTOR_RATIO_RANGE,
      MOTOR_RATIO_RANGE.min
    ),
  };
};

/** 界面模型 → MotorType 字节；模型没带 motorDuration 时沿用 previous 的 bit3-5 */
export const encodeMotorType = (
  { motorPressEnabled, motorReleaseEnabled, motorDuration, motorRatio } = {},
  previous = 0
) => {
  const duration =
    motorDuration != null && Number.isFinite(Number(motorDuration))
      ? clamp(motorDuration, MOTOR_DURATION_RANGE, MOTOR_DURATION_RANGE.min)
      : (toByte(previous) & MOTOR_DURATION_MASK) >> MOTOR_DURATION_SHIFT;
  return (
    ((motorPressEnabled ? 0 : MOTOR_PRESS_OFF_BIT) |
      (motorReleaseEnabled ? 0 : MOTOR_RELEASE_OFF_BIT) |
      (duration << MOTOR_DURATION_SHIFT) |
      clamp(motorRatio, MOTOR_RATIO_RANGE, MOTOR_RATIO_RANGE.min)) &
    0xff
  );
};

/** 按下或放开任一开启时，才能调比例和微调 */
export const canAdjustMotorStrength = (side) =>
  !!(side?.motorPressEnabled || side?.motorReleaseEnabled);

const readBit = (value, bit) => ((toByte(value) >> bit) & 1) === 1;

const writeBit = (value, bit, on) =>
  on ? (toByte(value) | (1 << bit)) & 0xff : (toByte(value) & ~(1 << bit)) & 0xff;

export const isKnownButtonType = (buttonType) =>
  Object.prototype.hasOwnProperty.call(
    BUTTON_TYPE_LABELS,
    decodeButtonTypeByte(buttonType).baseType
  );

export const getButtonTypeLabel = (buttonType) => {
  const { baseType } = decodeButtonTypeByte(buttonType);
  return BUTTON_TYPE_LABELS[baseType] ?? `未知类型 (${baseType})`;
};

export const getButtonModeLabel = (mode) =>
  BUTTON_MODE_LABELS[Number(mode)] ?? `未知模式 (${mode})`;

/**
 * 共板机型（bit0-3 = 2）的左右键可能是不同的物理微动，固件无法自动识别，
 * 必须由驱动分别写死左右键模式，因此这类机型不提供「自动」。
 */
export const supportsAutoMode = (buttonType) =>
  decodeButtonTypeByte(buttonType).baseType !==
  BUTTON_TYPE.MECHANICAL_OPTICAL_SHARED;

/**
 * bit0-3 为机械（0）或光微动（1）的机型微动是固定焊死的，识别方式由固件
 * 决定，驱动侧不允许切换按键模式。
 */
export const isButtonModeLocked = (buttonType) => {
  const { baseType } = decodeButtonTypeByte(buttonType);
  return (
    baseType === BUTTON_TYPE.MECHANICAL || baseType === BUTTON_TYPE.OPTICAL
  );
};

/**
 * 某一侧按键当前实际的微动类型：固定机型（bit0-3 = 0/1）直接取类型位，
 * 可换模块机型取该侧的模块位（bit4-5 / bit6-7）。
 * @param {number} buttonType - byte27 原值
 * @param {'left'|'right'} side
 */
export const resolveSideModule = (buttonType, side) => {
  const { baseType, leftModule, rightModule } = decodeButtonTypeByte(buttonType);
  if (isButtonModeLocked(buttonType)) return baseType;
  return side === 'right' ? rightModule : leftModule;
};

/**
 * 快速响应（fastButton）只对光微动开放。固定机型看类型位；可换模块
 * 机型以该侧所选识别模式为准（红外按键即光微动），「自动」时退回
 * 模块位判断——固件没填模块位时用户仍能靠切模式拿到开关。
 */
export const supportsFastResponse = (buttonType, side, mode) => {
  if (isButtonModeLocked(buttonType)) {
    return resolveSideModule(buttonType, side) === BUTTON_TYPE.OPTICAL;
  }
  const modeNumber = Number(mode);
  if (modeNumber === BUTTON_MODE.INFRARED) return true;
  if (modeNumber === BUTTON_MODE.AUTO) {
    return resolveSideModule(buttonType, side) === BUTTON_TYPE.OPTICAL;
  }
  return false;
};

/**
 * 强制延时（fastButtonEnable bit3/bit4）只作用于非机械按键。
 * 与快速响应同一套判定：固定机型与「自动」看物理模块，
 * 其余按所选识别模式算。
 */
export const supportsForceDelay = (buttonType, side, mode) => {
  if (isButtonModeLocked(buttonType) || Number(mode) === BUTTON_MODE.AUTO) {
    return resolveSideModule(buttonType, side) !== BUTTON_TYPE.MECHANICAL;
  }
  return Number(mode) !== BUTTON_MODE.MECHANICAL;
};

/**
 * RT 触碰点（按下/回弹触发深度）只对模拟行程生效：
 * 识别模式是「模拟按键」，或压力按键机型走「自动」。
 */
export const supportsRapidTrigger = (buttonType, mode) =>
  Number(mode) === BUTTON_MODE.ANALOG ||
  (Number(mode) === BUTTON_MODE.AUTO &&
    decodeButtonTypeByte(buttonType).baseType === BUTTON_TYPE.PRESSURE);

/**
 * 虚拟按键独立页：byte27 bit0-3 ≥ 3（霍尔 / 压力及以后的类型）。
 * 这类机型才提供触碰点、马达、马达校准与 ADC 行程。
 */
export const VIRTUAL_KEYS_MIN_BASE_TYPE = BUTTON_TYPE.HALL;

export const supportsVirtualKeys = (buttonType) =>
  decodeButtonTypeByte(buttonType).baseType >= VIRTUAL_KEYS_MIN_BASE_TYPE;

/** type3 回读 → 虚拟按键页是否可用（不看机型能力位） */
export function readVirtualKeysSupport(performanceData) {
  const buttonType = performanceData?.type3?.parsed?.buttonType;
  if (buttonType == null) {
    return { ready: false, supported: false };
  }
  return {
    ready: true,
    supported: supportsVirtualKeys(buttonType),
  };
}

/** 机械触点才需要防抖；模拟按键按行程判定，没有防抖概念 */
export const supportsDebounce = (buttonType, mode) =>
  Number(mode) !== BUTTON_MODE.ANALOG;

/**
 * 快速响应就是「按下不等防抖直接上报」，所以开启后按下防抖时间不可设置，
 * 只剩放开防抖可调。
 */
export const canSetPressDebounce = (side) => !side?.fastResponse;

/**
 * 「只有一样才可以替换按键相关数据」：左右键模式一致时才能作为一个整体
 * 联动下发，否则必须分别配置。
 */
export const canLinkButtons = (leftMode, rightMode) =>
  Number(leftMode) === Number(rightMode);

/* ---------------------------- 按键扫描频率 ----------------------------- */

/**
 * byte26：bit7 = 1 使能按键扫描频率，bit0-2 是频率档位。档位是扫描频率
 * 自己的编码，与回报率档位无关：0 → 1KHz、1 → 2KHz、2 → 4KHz、3 → 8KHz。
 * bit7 清零时扫描频率不生效，低位保留原值。
 */
export const SCAN_RATE_ENABLE_BIT = 0x80;
export const SCAN_RATE_LEVEL_MASK = 0x07;

/** bit0-2 档位 → 扫描频率（Hz） */
export const SCAN_RATE_LEVELS_HZ = Object.freeze([1000, 2000, 4000, 8000]);

export const scanRateLevelToHz = (level) =>
  SCAN_RATE_LEVELS_HZ[Number(level)] ?? null;

export const getScanRateLabel = (level) => {
  const hz = scanRateLevelToHz(level);
  return hz ? `${hz / 1000} KHz` : `未知档位 (${level})`;
};

/** byte26 → { enabled, level } */
export const decodeScanRate = (byte) => {
  const value = toByte(byte);
  return {
    enabled: (value & SCAN_RATE_ENABLE_BIT) !== 0,
    level: value & SCAN_RATE_LEVEL_MASK,
  };
};

/** { enabled, level } → byte26 */
export const encodeScanRate = ({ enabled, level }) =>
  ((enabled ? SCAN_RATE_ENABLE_BIT : 0) |
    (toByte(level) & SCAN_RATE_LEVEL_MASK)) &
  0xff;

/** 可选的扫描频率档位：协议固定 4 档（1/2/4/8 KHz） */
export function resolveScanRateLevels() {
  return SCAN_RATE_LEVELS_HZ.map((_, level) => level);
}

/* -------------------------------- 编解码 -------------------------------- */

/**
 * 把 type3 解析结果转成界面用的按键性能模型。
 * @param {Object} parsed - DeviceDataStore.parseV4PerformanceType3 的结果
 * @returns {Object|null}
 */
export function decodeButtonPerformance(parsed) {
  if (!parsed || parsed.buttonType == null) return null;

  const buttonType = toByte(parsed.buttonType);
  const buttonTypeBits = decodeButtonTypeByte(buttonType);
  const fastButtonEnable = toByte(parsed.fastButtonEnable);
  const scanRate = decodeScanRate(parsed.keyScanRate);
  const decodeSide = (side) => {
    const motorType = toByte(
      parsed[`${side}MotorType`],
      BUTTON_PERFORMANCE_DEFAULTS.motorType
    );
    return {
      mode: toByte(parsed[`${side}ButtonMode`]),
      // 该侧当前的物理模块，用于检测用户是否更换了微动模块
      module: resolveSideModule(buttonType, side),
      fastResponse: readBit(
        fastButtonEnable,
        FAST_BUTTON_BITS[`${side}FastResponse`]
      ),
      forceDelay: readBit(
        fastButtonEnable,
        FAST_BUTTON_BITS[`${side}ForceDelay`]
      ),
      debouncePress: toByte(
        parsed[`${side}DebouncetimePress`],
        BUTTON_PERFORMANCE_DEFAULTS.debouncePress
      ),
      debounceFree: toByte(
        parsed[`${side}DebouncetimeFree`],
        BUTTON_PERFORMANCE_DEFAULTS.debounceFree
      ),
      // RT 触碰点：按下/回弹的触发深度挡位
      rtPress: toByte(
        parsed[`${side}ButtonRTPress`],
        BUTTON_PERFORMANCE_DEFAULTS.rtPress
      ),
      rtFree: toByte(
        parsed[`${side}ButtonRTFree`],
        BUTTON_PERFORMANCE_DEFAULTS.rtFree
      ),
      motorType,
      ...decodeMotorType(motorType),
      motorLevel: toByte(
        parsed[`${side}MotorLevel`],
        BUTTON_PERFORMANCE_DEFAULTS.motorLevel
      ),
    };
  };

  const left = decodeSide('left');
  const right = decodeSide('right');

  return {
    // byte27 原值与位域拆解：baseType 是 bit0-3，模块位用于换模块检测
    buttonType,
    ...buttonTypeBits,
    modeLocked: isButtonModeLocked(buttonType),
    // 位域原值：写回时保留协议未定义的其余位
    fastButtonEnable,
    // bit7 使能位与 bit0-2 档位分开进模型
    scanRateEnabled: scanRate.enabled,
    scanRateLevel: scanRate.level,
    buttonTypeLabel: getButtonTypeLabel(buttonType),
    autoModeSupported: supportsAutoMode(buttonType),
    virtualKeysSupported: supportsVirtualKeys(buttonType),
    left,
    right,
    other: {
      debouncePress: toByte(
        parsed.otherDebouncetimePress,
        BUTTON_PERFORMANCE_DEFAULTS.debouncePress
      ),
      debounceFree: toByte(
        parsed.otherDebouncetimeFree,
        BUTTON_PERFORMANCE_DEFAULTS.debounceFree
      ),
    },
    scanRateLevels: resolveScanRateLevels(),
    linkable: canLinkButtons(left.mode, right.mode),
  };
}

/** 界面模型 → 协议字段值，写包与本地回填共用同一份换算 */
export function toButtonPerformanceFields(settings) {
  const { left, right, other } = settings;
  let fastButtonEnable = toByte(settings.fastButtonEnable);
  fastButtonEnable = writeBit(
    fastButtonEnable,
    FAST_BUTTON_BITS.leftFastResponse,
    !!left?.fastResponse
  );
  fastButtonEnable = writeBit(
    fastButtonEnable,
    FAST_BUTTON_BITS.rightFastResponse,
    !!right?.fastResponse
  );
  fastButtonEnable = writeBit(
    fastButtonEnable,
    FAST_BUTTON_BITS.leftForceDelay,
    !!left?.forceDelay
  );
  fastButtonEnable = writeBit(
    fastButtonEnable,
    FAST_BUTTON_BITS.rightForceDelay,
    !!right?.forceDelay
  );

  const sideFields = (side, prefix) => {
    const rtPress = clamp(
      side?.rtPress,
      RT_RANGE,
      BUTTON_PERFORMANCE_DEFAULTS.rtPress
    );
    return {
      [`${prefix}ButtonMode`]: toByte(side?.mode),
      [`${prefix}DebouncetimePress`]: clamp(
        side?.debouncePress,
        DEBOUNCE_RANGE,
        BUTTON_PERFORMANCE_DEFAULTS.debouncePress
      ),
      [`${prefix}DebouncetimeFree`]: clamp(
        side?.debounceFree,
        DEBOUNCE_RANGE,
        BUTTON_PERFORMANCE_DEFAULTS.debounceFree
      ),
      // RT 触碰点（触发深度）与马达：开关/比例在 MotorType，微调在 MotorLevel
      [`${prefix}ButtonRTPress`]: rtPress,
      // 回弹档位不能高于按下档位
      [`${prefix}ButtonRTFree`]: Math.min(
        clamp(side?.rtFree, RT_RANGE, BUTTON_PERFORMANCE_DEFAULTS.rtFree),
        rtPress
      ),
      [`${prefix}MotorType`]: encodeMotorType(side, side?.motorType),
      [`${prefix}MotorLevel`]: clamp(
        side?.motorLevel,
        MOTOR_LEVEL_RANGE,
        BUTTON_PERFORMANCE_DEFAULTS.motorLevel
      ),
    };
  };

  return {
    keyScanRate: encodeScanRate({
      enabled: settings.scanRateEnabled,
      level: settings.scanRateLevel,
    }),
    fastButtonEnable,
    ...sideFields(left, 'left'),
    ...sideFields(right, 'right'),
    otherDebouncetimePress: clamp(
      other?.debouncePress,
      DEBOUNCE_RANGE,
      BUTTON_PERFORMANCE_DEFAULTS.debouncePress
    ),
    otherDebouncetimeFree: clamp(
      other?.debounceFree,
      DEBOUNCE_RANGE,
      BUTTON_PERFORMANCE_DEFAULTS.debounceFree
    ),
  };
}

/**
 * 把按键性能字段写进已经克隆好的 type3 包。byte27（按键类型）是只读字段，
 * 保持设备回读的原值。
 *
 * @param {Uint8Array} packet - 由设备回读包克隆出来的 64 字节缓冲
 * @param {Object} settings - 界面模型
 * @returns {Object} 实际写入的字段值
 */
export function applyButtonPerformanceToPacket(packet, settings) {
  assertType3Packet(packet);

  const fields = toButtonPerformanceFields(settings);
  Object.entries(fields).forEach(([field, value]) => {
    const offset = V4_TYPE3_OFFSETS[field];
    if (offset == null) return;
    packet[offset] = toByte(value);
  });
  return fields;
}
